import classNames from "classnames";
import { useState } from "react";
import styles from "./Button.module.css";
import React from "react";

export interface ButtonProps {
  /**
   * Button component can return an `a` or a `button`
   */
  as?: "button" | "a";
  href?: string;
  title?: string;
  target?: string;
  buttonId?: string;

  buttonStyle?:
  | "tag"
  | "chevron"
  | "g-ol"
  | "w-ol"
  | "g-ol-ghost"
  | "g-full"
  | "g-exp"
  | string;

  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties,
}

/**
 * <Button /> component can be rendered `as` either a `button`
 * or an `a`. This is useful when using the button within a `<Link />`
 * component that requires an `a` to be the direct child of the `Link`.
 *
 * When the button must expand to show more text, use a double pipe `||`
 * to be the split point of the normal button text, and the expanded text
 * Example:
 *
 * `Download||our sustainability brochure`
 */
const _Button = ({
  target = "",
  buttonId,
  as = "button",
  children,
  buttonStyle = "g-full",
  className = "",
  ...props
}: ButtonProps) => {
  const Component = as;
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const toggleHover = () => setIsHovered(!isHovered);

  let splitButton = [];

  if (typeof children === "string" && children.includes("||")) {
    splitButton = children.split("||");
  }

  return (
    <Component
      target={target}
      className={classNames(styles._button, styles[buttonStyle], className)}
      {...props}
      onMouseOver={toggleHover}
      onMouseOut={toggleHover}
      id={buttonId}
    >
      <div>
        {/* if this is a normal text button */}
        {splitButton.length < 1 && children}

        {/* if this is a button with an expanded hover */}
        {splitButton.length > 0 && (
          <span className={styles.expandedwrap}>
            {splitButton[0]}
            {isHovered && (
              <span className={styles.expandedspan}>
                &nbsp;
                {splitButton[1]}
              </span>
            )}
          </span>
        )}

        {buttonStyle === "chevron" && (
          <svg
            width="6"
            height="9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1.5.75 5.25 4.5 1.5 8.25"
              stroke="#84BD00"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </Component>
  );
};

export const Button = React.memo(_Button)
